import { useStore } from '@vromo/mobx-react';
import { EntityList, Group, Role } from '@vromo/models';
import newEntityListFrom from '@vromo/models/helpers';
import { useToasts } from '@vromo/ui';
import Button from '@vromo/ui/components/Button';
import Container from '@vromo/ui/components/Container';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Header from '@vromo/ui/components/Header';
import Input from '@vromo/ui/components/Input';
import Modal from '@vromo/ui/components/Modal';
import theme from '@vromo/ui/components/theme';
import Tooltip from '@vromo/ui/components/Tooltip';
import { debounce } from 'lodash';
import uniqBy from 'lodash/uniqBy';
import { action, autorun } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { update } from 'serializr';
import { RootStore } from '../stores/RootStore';
import { qaLabelTeams } from './../../../../common-qa-labels/teams';
import EditSelectedTeamForm from './forms/EditSelectedTeamForm';
import NewDashboardUserForm from './forms/NewDashboardUserForm';
import NewDriverForm from './forms/NewDriverForm';
import NewTeamForm from './forms/NewTeamForm';
import SubTeamExplorer from './SubTeamExplorer';
import Team from './Team';
import styles from './teamEditor.style';

interface Props {
    roles: EntityList<Role>;
}

export type GroupTree = {
    group: Group;
    expanded: boolean;
    children: Array<GroupTree>;
};

enum ModalType {
    EditTeam,
    NewTeam,
    NewDriver,
    NewDashboardUser
}

const TeamEditor = observer(({ roles }: Props) => {
    const rootStore = useStore<RootStore>('rootStore');
    const { groupStore, vehicleStore } = rootStore;
    const { graph, role } = rootStore.authStore;

    const [selectedGroup, setSelectedGroup] = useState<Group>();
    const [parent, setParent] = useState<Group>();
    const [groupTree, setGroupTree] = useState<GroupTree>();
    const [filteredGroupTree, setFilteredGroupTree] = useState<GroupTree>();
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [activeModal, setActiveModal] = useState<ModalType | null>(null);

    const { t } = useTranslation();
    const showToast = useToasts();

    useEffect(() => {
        return autorun(() => {
            const { childDictionary } = groupStore;
            let rootGroup = groupStore.groups.first();
            childDictionary.forEach((children, id) => {
                if (rootGroup?.id) {
                    const currentRootChildren = childDictionary.get(rootGroup.id);

                    if (currentRootChildren && currentRootChildren.length < children.length) {
                        rootGroup = groupStore.withId(id);
                    }
                }
            });
            if (!rootGroup) return;

            setSelectedGroup(rootGroup);
            setParent(rootGroup);

            const tree = constructGroupTree(rootGroup);
            setGroupTree(tree);
            setFilteredGroupTree(tree);
        });
    }, []);

    const constructGroupTree = (group: Group): GroupTree => {
        const children = groupStore.getChildren(group);
        const directChildren = uniqBy(
            children.filter((c) => c.parents?.some((p) => p.id === group.id)),
            (g) => g.id
        );

        return {
            group,
            expanded: group.id === parent?.id,
            children: directChildren.map((c) => constructGroupTree(c))
        };
    };

    const openDriverModal = () => setActiveModal(ModalType.NewDriver);
    const openInviteModal = () => setActiveModal(ModalType.NewDashboardUser);
    const openNewTeamModal = () => setActiveModal(ModalType.NewTeam);
    const openTeamEditModal = () => setActiveModal(ModalType.EditTeam);
    const closeModal = () => setActiveModal(null);

    const editTeamName = (name: string) => {
        const editedGroup = newEntityListFrom<Group>(Group, [
            { id: selectedGroup!.id, name } as Group
        ]);

        try {
            graph.root
                .graph()
                .roles(role.id)
                .groups()
                .post(editedGroup)
                .then(
                    action(({ items }) => {
                        update(groupStore.groups, { items, type: 'update' });
                    })
                );
        } catch (e) {
            console.error(e);
            showToast('Could not edit the team name.', 'error');
        }

        closeModal();
    };

    const containsTerm = useCallback(
        (group: Group, term: string) => {
            const hasTerm = (s: string | null | undefined): boolean =>
                !!s?.toLowerCase().includes(term);
            if (hasTerm(group.name)) return true;
            const teamRolesWithMatch = roles.filter(({ account, user }) => {
                return account?.id === group.id && hasTerm(user?.displayName);
            });
            if (teamRolesWithMatch.length > 0) return true;
            const teamVehiclesWithMatch = vehicleStore.withGroup(group.id!).filter((vehicle) => {
                return hasTerm(vehicle.driver?.displayName);
            });
            return teamVehiclesWithMatch.length > 0;
        },
        [roles, vehicleStore]
    );

    const filterGroupTree = useCallback(
        (tree: GroupTree, searchTerm: string): GroupTree => {
            const findNode = (node: GroupTree): boolean => {
                return (
                    containsTerm(node.group, searchTerm) ||
                    (node.children.length > 0 && !!node.children.find((child) => findNode(child)))
                );
            };

            const filtered = tree.children
                .filter((c) => findNode(c))
                .map((c) => filterGroupTree(c, searchTerm));

            return {
                ...tree,
                ...{
                    expanded: searchTerm.length > 0 && filtered.length > 0,
                    children: filtered
                }
            };
        },
        [containsTerm]
    );

    const handleChangeSearchTerm = (e: ChangeEvent<HTMLInputElement>) => {
        const newSearchTerm = e.target.value?.toLowerCase();
        setSearchTerm(newSearchTerm);
    };

    const clearSearchTerm = () => setSearchTerm('');

    const filter = useCallback(() => {
        if (!groupTree) return;
        const filtered = filterGroupTree(groupTree, searchTerm);
        setFilteredGroupTree(filtered);
    }, [filterGroupTree, groupTree, searchTerm]);

    const delayedFilter = useMemo(() => debounce(filter, 500), [filter]);

    useEffect(() => {
        if (searchTerm) delayedFilter();
        else filter();
        return delayedFilter.cancel;
    }, [delayedFilter, filter, searchTerm]);

    const navigate = (g: Group) => setSelectedGroup(g);

    const isShowingSearch = !!(groupStore?.groups?.length > 10);

    const { authStore } = useStore<RootStore>('rootStore');
    const isEnterpriseUser = authStore.isEnterprise;

    return (
        <div className={styles.container}>
            <Container>
                <div className={styles.topContainer}>
                    <Header>{selectedGroup ? selectedGroup.name : ''}</Header>
                    {!isEnterpriseUser ? (
                        <div className={styles.topContent}>
                            <Button
                                data-testid={qaLabelTeams.editTeamButton}
                                style={{ backgroundColor: theme.primary }}
                                onClick={openTeamEditModal}>
                                {t('teams.editTeam')}
                            </Button>
                            <Button
                                data-testid={qaLabelTeams.newTeamButton}
                                onClick={openNewTeamModal}>
                                {t('teams.newTeam')}
                            </Button>
                            <Button
                                data-testid={qaLabelTeams.newDriverButton}
                                onClick={openDriverModal}>
                                {t('teams.newDriver')}
                            </Button>
                            <Button
                                data-testid={qaLabelTeams.newDashboardUserButton}
                                onClick={openInviteModal}>
                                {t('teams.newDashboardUser')}
                            </Button>
                        </div>
                    ) : (
                        <div className={styles.readOnlyContainer}>
                            <Tooltip position="bottom" label={t('teams.readOnlyTooltip')}>
                                <div className={styles.readOnly}>
                                    <FontAwesome icon="info-circle" className={styles.infoIcon} />
                                    <div>{t('teams.readOnly')}</div>
                                </div>
                            </Tooltip>
                        </div>
                    )}
                </div>
            </Container>
            <div className={styles.leftColumn}>
                <Container className={styles.leftContentList} testId="group-container">
                    <div>
                        {isShowingSearch && (
                            <Input
                                placeholder={t('teams.searchTeamPlaceholder')}
                                style={{ marginBottom: 10 }}
                                onChange={handleChangeSearchTerm}
                                value={searchTerm}
                                onClearClick={clearSearchTerm}
                            />
                        )}
                        {!!filteredGroupTree?.group?.id && (
                            <SubTeamExplorer
                                key={filteredGroupTree?.group?.id}
                                groupTree={filteredGroupTree}
                                depth={0}
                                selected={selectedGroup?.id}
                                navigate={navigate}
                            />
                        )}
                    </div>
                </Container>
                <Container className={styles.rightContent}>
                    {!!selectedGroup?.id && (
                        <Team
                            group={selectedGroup}
                            key={selectedGroup.id}
                            roles={roles}
                            searchTerm={searchTerm}
                            vehicles={vehicleStore.withGroup(selectedGroup.id)}
                        />
                    )}
                    <Modal isOpen={activeModal === ModalType.EditTeam} onRequestClose={closeModal}>
                        <EditSelectedTeamForm
                            name={selectedGroup?.name ?? ''}
                            save={editTeamName}
                            close={closeModal}
                        />
                    </Modal>
                    <Modal isOpen={activeModal === ModalType.NewTeam} onRequestClose={closeModal}>
                        <NewTeamForm close={closeModal} />
                    </Modal>
                    <Modal isOpen={activeModal === ModalType.NewDriver} onRequestClose={closeModal}>
                        <NewDriverForm close={closeModal} />
                    </Modal>
                    <Modal
                        isOpen={activeModal === ModalType.NewDashboardUser}
                        onRequestClose={closeModal}>
                        <NewDashboardUserForm roles={roles} close={closeModal} />
                    </Modal>
                </Container>
            </div>
        </div>
    );
});

export default TeamEditor;
